import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * A custom dropdown for the filter bar — the Month and Year pickers on In-Laws
 * Birthdays. Styled as one of the sort buttons, so it sits in the bar as one of
 * its controls, with a plain list of options under it in place of the
 * browser's native select.
 *
 * The bar it lives in scrolls sideways, and a scroller clips anything hanging
 * out of it — so the menu is portaled to the body and pinned in viewport
 * coordinates under the button. It closes on a click anywhere else, on
 * Escape, and whenever the page scrolls or resizes, since the button it hangs
 * from has moved.
 *
 * @param {object} props
 * @param {string} props.label — what the picker picks ("Month"); names the
 *   button for assistive tech and titles the list, not printed on screen — the
 *   value showing on the button says what the picker is
 * @param {Array<{value: string|number, label: string}>} props.options
 * @param {string|number} props.value
 * @param {(value: string|number) => void} props.onChange
 * @param {string} [props.className]
 */
export default function DashboardPeriodDropdown({
  label,
  options,
  value,
  onChange,
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, minWidth: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((option) => String(option.value) === String(value));

  const placeMenu = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    // Kept on screen: a button near the right edge opens its menu leftwards.
    const width = Math.max(rect.width, 120);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setMenuPosition({ top: rect.bottom + 4, left: Math.max(8, left), minWidth: width });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) placeMenu();
  }, [isOpen, placeMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      if (
        buttonRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    // Any scroll — the bar's own sideways one included — moves the button the
    // menu hangs from, so the menu closes rather than floating loose.
    const onScrollOrResize = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  // The selected option is scrolled into view when the list opens, so a long
  // list (the years) opens on the current choice rather than at its top. Only
  // the menu is scrolled: scrollIntoView also scrolls every box around it,
  // and on a phone that slid the report and its legend out of place.
  useEffect(() => {
    if (!isOpen) return;
    const menu = menuRef.current;
    const selectedEl = menu?.querySelector('[aria-selected="true"]');
    if (!menu || !selectedEl) return;
    const top = selectedEl.offsetTop;
    const bottom = top + selectedEl.offsetHeight;
    if (top < menu.scrollTop) {
      menu.scrollTop = top;
    } else if (bottom > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = bottom - menu.clientHeight;
    }
  }, [isOpen]);

  const pick = (option) => {
    setIsOpen(false);
    if (String(option.value) !== String(value)) onChange(option.value);
  };

  const onButtonKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const index = options.findIndex(
        (option) => String(option.value) === String(value)
      );
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = options[index + step];
      if (next) onChange(next.value);
    }
  };

  return (
    <div className={`dashboard-period-dropdown ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        className={`dashboard-detail-sort-btn dashboard-period-dropdown-button${
          isOpen ? " is-open" : ""
        }`}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        title={`Change ${label.toLowerCase()}`}
      >
        <span className="dashboard-period-dropdown-value">
          {selected ? selected.label : ""}
        </span>
        <svg
          className="dashboard-period-dropdown-chevron"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="dashboard-period-dropdown-menu"
            role="listbox"
            aria-label={label}
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              minWidth: `${menuPosition.minWidth}px`,
            }}
          >
            {options.map((option) => {
              const isSelected = String(option.value) === String(value);
              // An option may carry its own text colour — a family line in its
              // line colour. The selected option keeps the white-on-blue fill.
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`dashboard-period-dropdown-item${
                    isSelected ? " is-selected" : ""
                  }${option.color ? " has-line-color" : ""}`}
                  style={
                    option.color
                      ? { "--dashboard-period-item-color": option.color }
                      : undefined
                  }
                  onClick={() => pick(option)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
